/**
 * Button — shadcn/ui primitive (copied + owned). Tokens per
 * docs/design/2026-07-10_sebastian-package/design.md §5 (four tiers) + §2.
 * Backward-compatible with the M1 skeleton API (variant/size incl. "md").
 *
 * Tier ↔ variant mapping (restyle in place — existing keys kept):
 *   Primary   = `default` (alias `primary`)   — filled --primary blue, white
 *               font-display Medium label, italic-on-press (brand signature).
 *   Secondary = `outline`                     — white/card fill + --border, ink label.
 *   Tertiary  = `secondary` (alias `tertiary`)— subtle --secondary fill, no border.
 *   Ghost     = `ghost`                       — text only, muted.
 *   `destructive` stays filled red (§2 priority ladder #1: danger beats brand);
 *   `link` unchanged.
 *
 * §2/§5 rules for consumers: exactly ONE Primary action per context — everything
 * else Secondary/Ghost. Hover darkens the surface only (label stays upright).
 * Disabled is muted, never italic. §5 exception (a U3 consumer note): a green
 * filled "Freigeben/Release" button is allowed as the positive terminal action
 * of a review flow.
 *
 * Italic-on-press (Primary only): the real GT Pressura italic is wider than the
 * upright cut, so a string label renders with an aria-hidden invisible italic
 * twin that pre-reserves the italic width (no reflow on press). Non-string
 * children fall back to plain `active:italic` (documented, acceptable). Until
 * the GT Pressura license lands the fallback is synthesized-oblique Geist,
 * which keeps advance widths — the guard is a no-op until then.
 */
import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

const primaryClasses =
  'bg-primary text-primary-foreground hover:bg-primary/90 shadow-xs font-display';
const tertiaryClasses =
  'bg-secondary text-secondary-foreground hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)]';

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: primaryClasses,
        primary: primaryClasses,
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-xs',
        outline: 'border border-border bg-card text-foreground hover:bg-secondary',
        secondary: tertiaryClasses,
        tertiary: tertiaryClasses,
        ghost: 'text-muted-foreground hover:bg-secondary hover:text-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        md: 'h-9 px-4 py-2',
        lg: 'h-10 rounded-md px-6',
        icon: 'size-9',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, children, disabled, ...props }, ref) => {
    const isPrimary = variant == null || variant === 'default' || variant === 'primary';
    return (
      <button
        ref={ref}
        disabled={disabled}
        className={cn(
          buttonVariants({ variant, size }),
          // Press signature: italic on :active — Primary only, never on hover,
          // never when disabled (class dropped entirely so a disabled Primary
          // carries no italic styling at all).
          isPrimary && !disabled && 'active:italic',
          className,
        )}
        {...props}
      >
        {isPrimary && typeof children === 'string' ? (
          <span className="inline-flex flex-col">
            <span>{children}</span>
            {/* Invisible italic twin: pre-reserves the (wider) italic width so the
                label never shifts on press. aria-hidden keeps the accessible name
                exact (label appears once). */}
            <span aria-hidden="true" className="invisible h-0 overflow-hidden italic">
              {children}
            </span>
          </span>
        ) : (
          children
        )}
      </button>
    );
  },
);
Button.displayName = 'Button';
